import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { getGrooverText } from '../../libs/GrooverTextRepository'
import useAppState from '../../hooks/useAppState'
import { getPortalBrandingCssVars, usePortalBranding } from '../../hooks/usePortalBranding'

export enum PortalLoginLoadingContext {
  SendCode = 'sendCode',
  VerifyCode = 'verifyCode',
  PreparePortal = 'preparePortal',
  CompleteEmailLink = 'completeEmailLink'
}

const MESSAGE_KEYS_BY_CONTEXT: Record<PortalLoginLoadingContext, string[]> = {
  [PortalLoginLoadingContext.SendCode]: [
    'portalLoginLoadingWarmUp',
    'portalLoginLoadingLookup',
    'portalLoginLoadingSecurity',
    'portalLoginLoadingBots',
    'portalLoginLoadingSms',
    'portalLoginLoadingSmsAlmost'
  ],
  [PortalLoginLoadingContext.VerifyCode]: [
    'portalLoginLoadingVerify',
    'portalLoginLoadingSignIn',
    'portalLoginLoadingWelcomeBack'
  ],
  [PortalLoginLoadingContext.PreparePortal]: [
    'portalLoginLoadingPrepare',
    'portalLoginLoadingDashboard',
    'portalLoginLoadingAlmostThere'
  ],
  [PortalLoginLoadingContext.CompleteEmailLink]: [
    'portalLoginLoadingVerify',
    'portalLoginLoadingSignIn',
    'portalLoginLoadingWelcomeBack'
  ]
}

const MESSAGE_CYCLE_MS = 2200

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(var(--portal-primary-rgb, 124, 58, 237), 0.1);
  border-top-color: var(--portal-primary, #7c3aed);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const LoadingMessage = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: #64748b;
  margin: 0;
  text-align: center;
  max-width: 300px;
  animation: portalLoginMessageFadeIn 0.45s ease;

  @keyframes portalLoginMessageFadeIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

interface PortalLoginLoadingViewProps {
  context: PortalLoginLoadingContext
}

export const PortalLoginLoadingView: React.FC<PortalLoginLoadingViewProps> = ({ context }) => {
  const { state } = useAppState()
  const selectedLanguage = state.selectedLanguage
  const messageKeys = MESSAGE_KEYS_BY_CONTEXT[context]
  const [messageIndex, setMessageIndex] = useState(0)
  /**
   * This view renders *instead of* `PortalLayout` while `ProtectedRoute` waits for auth claims, so
   * it is the first thing a returning visitor sees on refresh — and nothing else on that path
   * carries the branding variables. Resolving branding here is what keeps the spinner on-brand
   * rather than Coursely purple for the duration of the gate.
   */
  const branding = usePortalBranding()

  useEffect(() => {
    setMessageIndex(0)
  }, [context])

  useEffect(() => {
    if (messageKeys.length <= 1) {
      return
    }

    const timer = setInterval(() => {
      setMessageIndex((current) => (current + 1) % messageKeys.length)
    }, MESSAGE_CYCLE_MS)

    return () => clearInterval(timer)
  }, [messageKeys])

  const messageKey = messageKeys[messageIndex]

  return (
    <LoadingWrapper style={getPortalBrandingCssVars(branding)}>
      <LoadingSpinner />
      <LoadingMessage key={messageKey}>{getGrooverText(messageKey, selectedLanguage)}</LoadingMessage>
    </LoadingWrapper>
  )
}
